import type { SpecType } from '@local/lib';

import { ProductPriceBasisType } from '@local/lib';
import { Collapse, NumberInput, Select } from '@mantine/core';

import type { ProductPriceBasisState } from '../qualityFormulaTypes';

import { toNumberOrDefault } from '../numberInputValue';
import { FIELD_HELP, PRODUCT_PRICE_BASIS_OPTIONS } from '../qualityFormulaMetadata';
import { FieldLabelWithHelp } from './FieldLabelWithHelp';
import { PriceComponentsTable } from './PriceComponentsTable';

/** Shared shape for Price Basis' two inline sub-fields (`ProductPriceBasisField`/`DeemedPriceField`) — see `SpecCardPriceBasisFieldSlot` in QualitySpecCard.tsx, which renders either against this same interface. */
export interface ProductPriceBasisSubFieldProps {
  /** Locks the field to its current value — used by the Template Editor, where Price Basis always
   * stays Contract Price; changing it to Deemed Price or Price Components is a Contract Entry
   * action only. */
  disabled?: boolean;
  onChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  priceBasis: ProductPriceBasisState;
  specType: SpecType;
}

/**
 * The "Price Basis" column rendered inline alongside Formula Type / Tier Mode / Decimal Precision
 * — a Select (not a SegmentedControl) so all four read as one uniform, compact row instead of
 * this field alone standing out or growing the card taller. Only ever rendered by QualitySpecCard
 * while `formulaType` is PERCENTAGE ("% of Price") — see business rule in SpecCardPriceBasisSlot.
 */
export const ProductPriceBasisField: React.FC<ProductPriceBasisSubFieldProps> = ({
  disabled,
  onChange,
  priceBasis,
  specType,
}) => (
  <Select
    data={PRODUCT_PRICE_BASIS_OPTIONS.map(option => ({ label: option.label, value: option.value }))}
    disabled={disabled}
    flex="1 1 160px"
    label={<FieldLabelWithHelp label="Price Basis" tooltip={FIELD_HELP.priceBasisType} />}
    onChange={value => {
      // Pre-existing cross-package enum-identity lint quirk — see api/pricingEngineApi.ts.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-assignment
      if (value) onChange(specType, { type: value as ProductPriceBasisType });
    }}
    value={priceBasis.type}
  />
);

/**
 * The Deemed Price amount, rendered as its own column immediately next to Price Basis (not below
 * the row) since it's the one Price Basis option with no other follow-up content — there's no
 * reason to grow the card taller for a single number. Only mounted while `priceBasis.type` is
 * DeemedPrice; see `SpecCardPriceBasisFieldSlot` in QualitySpecCard.tsx.
 */
export const DeemedPriceField: React.FC<ProductPriceBasisSubFieldProps> = ({ onChange, priceBasis, specType }) => (
  <NumberInput
    flex="1 1 160px"
    hideControls
    label={<FieldLabelWithHelp label="Deemed Price ($)" tooltip={FIELD_HELP.priceBasisDeemedPrice} />}
    min={0}
    onChange={value => {
      onChange(specType, { deemedPrice: toNumberOrDefault(value, 0) });
    }}
    value={priceBasis.deemedPrice}
  />
);

interface ProductPriceBasisDetailsProps {
  /** Lets the user collapse Price Components (see SpecSectionPanel's own doc comment) — only for
   * Range, where it stacks full-width alongside Price Adjustment Ranges; Threshold's split layout
   * already gives it its own column, so collapsing it there would serve no purpose. */
  collapsible?: boolean;
  /** Stretches Price Components to fill its sibling column's height (see SpecSectionPanel's own
   * doc comment) — only meaningful alongside that column, so callers outside QualitySpecCard's
   * split layout never pass this. */
  fillHeight?: boolean;
  onChange: (specType: SpecType, update: Partial<ProductPriceBasisState>) => void;
  priceBasis: ProductPriceBasisState;
  specType: SpecType;
}

/**
 * The follow-up content for Price Components — the only Price Basis option with content too
 * large to fit inline next to the Select (Contract Price needs nothing, Deemed Price is a single
 * inline field, see `DeemedPriceField`) — rendered full-width below the Formula Type row via
 * `Collapse` so it animates in/out instead of popping.
 */
export const ProductPriceBasisDetails: React.FC<ProductPriceBasisDetailsProps> = ({
  collapsible,
  fillHeight,
  onChange,
  priceBasis,
  specType,
}) => {
  const addComponent = (key: string) => {
    if (priceBasis.selectedComponentKeys.includes(key)) return;
    onChange(specType, { selectedComponentKeys: [...priceBasis.selectedComponentKeys, key] });
  };

  const removeComponent = (key: string) => {
    onChange(specType, {
      selectedComponentKeys: priceBasis.selectedComponentKeys.filter(existing => existing !== key),
    });
  };

  return (
    <Collapse
      in={priceBasis.type === ProductPriceBasisType.PriceComponents}
      style={fillHeight ? { height: '100%' } : undefined}
    >
      <PriceComponentsTable
        collapsible={collapsible}
        fillHeight={fillHeight}
        onAdd={addComponent}
        onRemove={removeComponent}
        selectedKeys={priceBasis.selectedComponentKeys}
      />
    </Collapse>
  );
};
